<template>
    <!-- Galería a pantalla completa de la ficha: vista "Foto" (una entera,
         centrada, con flechas, miniaturas y deslizar) o "Todas" (grilla).
         Teclado: ← → cambian, Esc cierra (o vuelve de la grilla), G alterna. -->
    <transition name="fx-fade">
        <div v-if="abierta" ref="caja" :class="['fx-lb', { 'is-grid': modo === 'grilla' }]" role="dialog" aria-modal="true" :aria-label="titulo" tabindex="-1">
            <header class="fx-lb-top">
                <div class="fx-lb-info">
                    <p class="fx-lb-title">{{ titulo }}</p>
                    <p class="fx-lb-sub">{{ subtitulo }}</p>
                </div>
                <div class="fx-lb-modes" role="group" :aria-label="$t('ficha_nueva.gal.view')">
                    <button type="button" class="fx-lb-mode" :aria-pressed="String(modo === 'foto')" @click="modo = 'foto'">{{ $t('ficha_nueva.gal.photo') }}</button>
                    <button type="button" class="fx-lb-mode" :aria-pressed="String(modo === 'grilla')" @click="verGrilla">{{ $t('ficha_nueva.gal.all') }}</button>
                </div>
                <button ref="cerrar" type="button" class="fx-lb-x" :aria-label="$t('ficha_nueva.gal.close')" @click="cerrar">×</button>
            </header>

            <div v-show="modo === 'foto'" class="fx-lb-stage" @click.self="cerrar" @touchstart.passive="tx = $event.touches[0].clientX" @touchend="deslizar">
                <figure class="fx-lb-fig">
                    <img :key="i" :class="['fx-lb-img', dir > 0 ? 'from-r' : dir < 0 ? 'from-l' : 'is-fade']" :src="fotos[i]" :alt="$t('ficha_nueva.gal.photo_n', { n: i + 1, total: fotos.length })" />
                    <figcaption class="fx-lb-count">{{ i + 1 }} / {{ fotos.length }}</figcaption>
                </figure>
                <template v-if="fotos.length > 1">
                    <button type="button" class="fx-lb-nav fx-lb-nav--prev" :aria-label="$t('ficha_nueva.gal.prev')" @click="mover(-1)">
                        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m15 5-7 7 7 7" /></svg>
                    </button>
                    <button type="button" class="fx-lb-nav fx-lb-nav--next" :aria-label="$t('ficha_nueva.gal.next')" @click="mover(1)">
                        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7" /></svg>
                    </button>
                </template>
            </div>

            <div v-show="modo === 'foto'" ref="thumbs" class="fx-lb-thumbs">
                <button v-for="(f, n) in fotos" :key="'t' + n" type="button" :aria-current="String(n === i)" :aria-label="$t('ficha_nueva.gal.photo_n', { n: n + 1, total: fotos.length })" @click="ir(n)">
                    <img :src="f" alt="" loading="lazy" />
                </button>
            </div>

            <div v-show="modo === 'grilla'" ref="grilla" class="fx-lb-grid">
                <button v-for="(f, n) in fotos" :key="'g' + n" type="button" class="fx-lb-cell" :data-n="n" :aria-label="$t('ficha_nueva.gal.photo_n', { n: n + 1, total: fotos.length })" @click="ir(n); modo = 'foto'; $nextTick(() => $refs.cerrar && $refs.cerrar.focus())">
                    <img :src="f" alt="" loading="lazy" />
                </button>
            </div>
        </div>
    </transition>
</template>

<script>
export default {
    name: 'FxGaleria',
    props: {
        fotos: { type: Array, default: () => [] },   // URLs ya normalizadas
        titulo: { type: String, default: '' },
        subtitulo: { type: String, default: '' },
    },
    data() {
        return { abierta: false, i: 0, dir: 0, modo: 'foto', tx: null, volver: null };
    },
    methods: {
        abrir(n = 0) {
            if (!this.fotos.length) return;
            this.volver = document.activeElement;
            this.i = Math.min(Math.max(0, n), this.fotos.length - 1);
            this.dir = 0; this.modo = 'foto'; this.abierta = true;
            document.documentElement.classList.add('fx-lock');
            window.addEventListener('keydown', this.tecla);
            this.$nextTick(() => { if (this.$refs.cerrar) this.$refs.cerrar.focus(); this.centrarMiniatura(); });
        },
        cerrar() {
            this.abierta = false;
            window.removeEventListener('keydown', this.tecla);
            document.documentElement.classList.remove('fx-lock');
            if (this.volver && this.volver.focus) this.volver.focus();
        },
        mover(d) { this.dir = d; this.i = (this.i + d + this.fotos.length) % this.fotos.length; this.$nextTick(this.centrarMiniatura); this.precargar(); },
        ir(n) { this.dir = Math.sign(n - this.i); this.i = n; this.$nextTick(this.centrarMiniatura); },
        verGrilla() {
            this.modo = 'grilla';
            this.$nextTick(() => {
                const c = this.$refs.grilla && this.$refs.grilla.querySelector(`[data-n="${this.i}"]`);
                if (c) { c.scrollIntoView({ block: 'center' }); c.focus(); }
            });
        },
        centrarMiniatura() {
            const t = this.$refs.thumbs && this.$refs.thumbs.children[this.i];
            if (t && t.scrollIntoView) t.scrollIntoView({ block: 'nearest', inline: 'center' });
        },
        precargar() { const s = this.fotos[(this.i + 1) % this.fotos.length]; if (s) { const im = new Image(); im.src = s; } },
        deslizar(e) {
            if (this.tx == null) return;
            const d = e.changedTouches[0].clientX - this.tx;
            if (Math.abs(d) > 50) this.mover(d < 0 ? 1 : -1);
            this.tx = null;
        },
        tecla(e) {
            if (e.key === 'Escape') { e.preventDefault(); if (this.modo === 'grilla') { this.modo = 'foto'; this.$nextTick(() => this.$refs.cerrar && this.$refs.cerrar.focus()); } else this.cerrar(); return; }
            if (e.key === 'ArrowLeft' && this.modo === 'foto') return this.mover(-1);
            if (e.key === 'ArrowRight' && this.modo === 'foto') return this.mover(1);
            if ((e.key === 'g' || e.key === 'G') && !e.ctrlKey && !e.metaKey) return this.modo === 'foto' ? this.verGrilla() : (this.modo = 'foto');
            if (e.key === 'Tab') {
                // El foco no sale de la galería
                const f = [...this.$refs.caja.querySelectorAll('button')].filter(b => b.offsetParent !== null);
                if (!f.length) return;
                const [a, z] = [f[0], f[f.length - 1]];
                if (e.shiftKey && document.activeElement === a) { e.preventDefault(); z.focus(); }
                else if (!e.shiftKey && document.activeElement === z) { e.preventDefault(); a.focus(); }
            }
        },
    },
    beforeDestroy() { window.removeEventListener('keydown', this.tecla); document.documentElement.classList.remove('fx-lock'); },
};
</script>
